import { ColDef, themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import images from "../../../assets/images";
import Modal from "../../../components/modal/Modal";
import { useManufacturerListStore } from "../../../store/masterDataStore";
import { ManufacturerListType } from "../../../types/api";

const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
  headerColumnResizeHandleColor: "rgb(126, 46, 132)",
  fontSize: "0.75rem",
  headerFontWeight: "bold",
});

const emptyForm = {
  manufacturerName: "",
  contactPerson: "",
  phone: "",
  emailAddress: "",
  address: "",
  websiteUrl: "",
  notes: "",
};

const ManufacturerList: React.FC = () => {
  const navigate = useNavigate();
  const {
    manufacturerList,
    fetchAllManufacturerList,
    createManufacturerList,
    updateManufacturerList,
    deleteManufacturerList,
  } = useManufacturerListStore();
  const [rowData, setRowData] = useState<ManufacturerListType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [editId, setEditId] = useState<string | null>(null);
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await fetchAllManufacturerList();
      setIsLoading(false);
    };
    fetchData();
  }, []);

  useEffect(() => {
    setRowData(manufacturerList);
  }, [manufacturerList]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      let response;
      if (isEditMode && editId) {
        response = await updateManufacturerList({ ...formData }, editId);
      } else {
        response = await createManufacturerList(formData);
      }

      if (response) {
        setModalOpen(false);
        setFormData(emptyForm);
        setEditId(null);
        setIsEditMode(false);
        await fetchAllManufacturerList();
      }
    } catch (error) {
      console.error("Error creating manufacturer:", error);
    }
  };

  const handleEdit = useCallback(
    async (data: any) => {
      setFormData({
        manufacturerName: data.manufacturerName,
        contactPerson: data.contactPerson,
        phone: data.phone,
        emailAddress: data.emailAddress,
        address: data.address,
        websiteUrl: data.websiteUrl,
        notes: data.notes,
      });
      setEditId(data._id);
      setIsEditMode(true);
      setModalOpen(true);
    },
    [setFormData, setEditId, setIsEditMode, setModalOpen]
  );

  const handleDelete = async (id: string) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this manufacturer?"
    );
    if (confirmed) {
      await deleteManufacturerList(id);
    }
  };

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        field: "manufacturerName",
        headerName: "Manufacturer Name",
        sortable: false,
        minWidth: 20,
        flex: 1,
        headerStyle: {
          backgroundColor: "#e9f5ff",
        },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }),
      },
      {
        field: "contactPerson",
        headerName: "Contact Person",
        sortable: false,
        minWidth: 20,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
          whiteSpace: "nowrap" as const,
          overflow: "hidden" as const,
          textOverflow: "ellipsis" as const,
        }),
        tooltipField: "contactPerson",
      },
      {
        field: "phone",
        headerName: "Phone Number",
        sortable: false,
        minWidth: 20,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }),
      },
      {
        field: "action",
        headerName: "Action",
        cellStyle: { padding: "10px 15px 0 0 ", textAlign: "left" },
        // minWidth: 20,
        headerStyle: { backgroundColor: "#e9f5ff" },
        flex: 0,
        minWidth: 40,
        maxWidth: 90,
        cellRenderer: (params: any) => (
          <div className="flex gap-x-3 pt-2">
            <button
              onClick={() => handleEdit(params.data)}
              className="p-1 hover:bg-gray-100 rounded"
              title="Edit Manufacturer"
            >
              <img
                className="iconTd w-5 h-5"
                src={images.editIcon}
                alt="edit"
              />
            </button>
            <button
              onClick={() => handleDelete(params.data._id)}
              className="p-1 hover:bg-red-100 rounded"
              title="Delete Manufacturer"
            >
              <Trash2 className="text-red-500" size={23} />
            </button>
          </div>
        ),
        filter: false,
      },
    ],
    []
  );

  const defaultColDef = useMemo(() => {
    return {
      editable: false,
      filter: false,
      tooltipComponentParams: {
        color: "#ececec",
      },
    };
  }, []);

  const theme = useMemo(() => {
    return gridTheme;
  }, []);

  return (
    <>
      <div className="px-2 py-4 mt-14 min-h-screen">
        <div className="flex justify-between items-center mb-4">
          <h2 className="ml-2">Manufacturer List</h2>
          <button
            className="flex items-center justify-center font-semibold gap-2 bg-[#0fa9a6] text-white border border-[#0fa9a6] px-4 py-2 rounded-full hover:bg-teal-600"
            onClick={() => {
              setModalOpen(true);
              setIsEditMode(false);
              setFormData(emptyForm);
            }}
          >
            <Plus size={20} /> Add Manufacturer
          </button>
        </div>

        <div className="tableBlock w-full">
          <div className="card">
            {rowData?.length > 0 ? (
              <div className="ag-theme-alpine w-full min-h-[400px] border-0 rounded-lg shadow-lg">
                <AgGridReact
                  rowData={rowData}
                  columnDefs={columnDefs}
                  theme={theme}
                  headerHeight={65}
                  rowHeight={50}
                  loading={isLoading}
                  defaultColDef={defaultColDef}
                  domLayout="autoHeight"
                  pagination={true}
                  paginationPageSize={5}
                  paginationPageSizeSelector={[5, 10, 20]}
                  paginationAutoPageSize={false}
                />
              </div>
            ) : (
              <div className="flex flex-col justify-center items-center h-64">
                <p className="text-gray-500 text-lg mb-4">No data added yet</p>
              </div>
            )}
          </div>
        </div>
      </div>
      <Modal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        closeButton={false}
        modalClass="bg-white w-full max-w-3xl rounded-xl shadow-lg relative flex flex-col max-h-[80vh]"
      >
        <div className="pt-6 overflow-y-auto">
          <h2 className="text-xl font-semibold text-gray-800 border-b px-6 pb-2 mb-4">
            {isEditMode
              ? "Update Manufacturer Details"
              : "Create New Manufacturer Details"}
          </h2>
          <form onSubmit={handleSubmit} id="manufacturerForm">
            <div className="px-6 pb-6 flex flex-col gap-6">
              {/* Manufacturer Name */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="manufacturerName"
                  className="text-sm font-medium text-gray-700"
                >
                  Manufacturer Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="manufacturerName"
                  name="manufacturerName"
                  type="text"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  value={formData.manufacturerName}
                  onChange={handleChange}
                  placeholder="Enter manufacturer name"
                  required
                  aria-required="true"
                  aria-label="Manufacturer Name"
                />
              </div>

              {/* Contact Person */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="contactPerson"
                  className="text-sm font-medium text-gray-700"
                >
                  Contact Person <span className="text-red-500">*</span>
                </label>
                <input
                  id="contactPerson"
                  name="contactPerson"
                  type="text"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  value={formData.contactPerson}
                  onChange={handleChange}
                  placeholder="Enter contact person's name"
                  required
                  aria-required="true"
                  aria-label="Contact Person"
                />
              </div>

              {/* Phone Number */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="phone"
                  className="text-sm font-medium text-gray-700"
                >
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Enter phone number"
                  required
                  aria-required="true"
                  aria-label="Phone Number"
                />
              </div>

              {/* Email Address */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="emailAddress"
                  className="text-sm font-medium text-gray-700"
                >
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  id="emailAddress"
                  name="emailAddress"
                  type="email"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  value={formData.emailAddress}
                  onChange={handleChange}
                  placeholder="Enter email address"
                  required
                  aria-required="true"
                  aria-label="Email Address"
                />
              </div>

              {/* Address */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="address"
                  className="text-sm font-medium text-gray-700"
                >
                  Address
                </label>
                <textarea
                  id="address"
                  name="address"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none resize-y"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Enter full mailing address"
                  rows={3}
                  aria-label="Address"
                />
              </div>

              {/* Website URL */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="websiteUrl"
                  className="text-sm font-medium text-gray-700"
                >
                  Website URL
                </label>
                <input
                  id="websiteUrl"
                  name="websiteUrl"
                  type="url"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  value={formData.websiteUrl}
                  onChange={handleChange}
                  placeholder="Enter website URL"
                  aria-label="Website URL"
                />
              </div>

              {/* Notes / Remarks */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="notes"
                  className="text-sm font-medium text-gray-700"
                >
                  Notes / Remarks
                </label>
                <textarea
                  id="notes"
                  name="notes"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none resize-y"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Add any remarks or additional info"
                  rows={3}
                  aria-label="Notes"
                />
              </div>
            </div>
          </form>
        </div>
        <div className="h-12" />
        <div className="flex justify-end gap-3 p-2 bg-[#5A7192] w-full rounded-b-lg sticky bottom-0">
          <button
            type="button"
            className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-teal-600 transition"
            onClick={() => setModalOpen(false)}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="manufacturerForm"
            className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
          >
            {isEditMode ? "Update" : "Create"}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default ManufacturerList;
